import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, combineLatest, of, timer } from 'rxjs';
import { map, catchError, startWith, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environment/environment';
import { SiteCacheService } from '../site-cache/site-cache.service';

@Injectable({
  providedIn: 'root'
})
export class VersionService {
  private http = inject(HttpClient);
  private siteCacheService = inject(SiteCacheService);
  
  private backendVersionSubject = new BehaviorSubject<string>('Loading...');
  private serviceWorkerVersionSubject = new BehaviorSubject<string>('unknown');
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);
  
  public backendVersion$ = this.backendVersionSubject.asObservable();
  public serviceWorkerVersion$ = this.serviceWorkerVersionSubject.asObservable();
  public updateAvailable$ = this.updateAvailableSubject.asObservable();
  
    // Combined version info - prefers backend version, fallback to service worker
  public displayVersion$: Observable<string> = combineLatest([
    this.serviceWorkerVersion$,
    this.backendVersion$
  ]).pipe(
    map(([swVersion, backendVersion]) => {
      // Prioritize backend version since it's more reliable
      if (backendVersion && backendVersion !== 'Loading...' && backendVersion !== 'N/A') {
        // If it looks like a semantic version (contains dots), use as-is
        if (backendVersion.includes('.')) {
          return backendVersion;
        }
        // Otherwise treat as hash and use first 8 characters
        const shortHash = backendVersion.substring(0, 8);
        return shortHash;
      }
      if (swVersion && swVersion !== 'unknown') {
        // Use first 8 characters of service worker hash
        const shortSw = swVersion.substring(0, 8);
        return shortSw;
      }
      return 'N/A';
    }),
    distinctUntilChanged()
  );

  constructor() {
    // Listen to service worker version updates
    this.siteCacheService.currentVersion$.subscribe(version => {
      this.serviceWorkerVersionSubject.next(version);
    });

    // Listen to site cache service update availability
    this.siteCacheService.updateAvailable$.subscribe(available => {
      this.updateAvailableSubject.next(available);
    });

    // Fetch backend version on service initialization
    this.initializeBackendVersion();

    // Set up periodic version checking
    this.setupPeriodicVersionCheck();
  }

  /**
   * Fetch current version hash from backend
   */
  public fetchBackendVersion(): Observable<string> {
    return this.http.get<any>(`${environment.rootApiUrl}/cache_hash.php`, {
      withCredentials: false,
      params: {
        _t: Date.now().toString()
      }
    }).pipe(
      map(response => {
        // Handle different response formats
        if (response.hash) {
          return response.hash.toString();
        }
        if (response.version) {
          return response.version.toString();
        }
        // If it's just a string/number, return it
        return response.toString();
      }),
      catchError(error => {
        console.error('[VersionService] Failed to fetch backend version:', error);
        return of('N/A');
      })
    );
  }

  /**
   * Fetch and update backend version
   */
  private initializeBackendVersion(): void {
    this.fetchBackendVersion().subscribe(version => {
      this.backendVersionSubject.next(version);
    });
  }

  /**
   * Get current display version synchronously
   */
  public getCurrentDisplayVersion(): string {
    // Prioritize backend version
    const backendVersion = this.backendVersionSubject.value;
    if (backendVersion && backendVersion !== 'Loading...' && backendVersion !== 'N/A') {
      // If it looks like a semantic version (contains dots), use as-is
      if (backendVersion.includes('.')) {
        return backendVersion;
      }
      // Otherwise treat as hash and use first 8 characters
      return backendVersion.substring(0, 8);
    }
    
    const swVersion = this.serviceWorkerVersionSubject.value;
    if (swVersion && swVersion !== 'unknown') {
      return swVersion.substring(0, 8);
    }
    
    return 'N/A';
  }

  /**
   * Refresh backend version
   */
  public refreshBackendVersion(): void {
    this.initializeBackendVersion();
  }

  /**
   * Set up periodic version checking every 5 minutes
   */
  private setupPeriodicVersionCheck(): void {
    // Check for backend version updates every 5 minutes
    timer(0, 5 * 60 * 1000).pipe(
      switchMap(() => this.fetchBackendVersion()),
      tap(version => {
      })
    ).subscribe(version => {
      this.backendVersionSubject.next(version);
    });

    // Check for service worker updates every 10 minutes (reduced frequency)
    timer(0, 10 * 60 * 1000).subscribe(() => {
      if (this.siteCacheService.isSupported()) {
        this.siteCacheService.checkForUpdate().catch(error => {
          console.error('[VersionService] Error checking for SW updates:', error);
        });
      }
    });
  }

  /**
   * Check if there's a version mismatch that indicates an update is needed
   */
  public hasVersionMismatch(): Observable<boolean> {
    return combineLatest([
      this.backendVersion$,
      this.serviceWorkerVersion$
    ]).pipe(
      map(([backendVersion, swVersion]) => {
        // If we have both versions and they're different, there might be an update
        if (backendVersion !== 'Loading...' && backendVersion !== 'N/A' && 
            swVersion !== 'unknown' && backendVersion !== swVersion) {
          return true;
        }
        return false;
      }),
      distinctUntilChanged()
    );
  }

  /**
   * Force a complete refresh with cache clearing
   */
  public forceRefresh(): void {
    this.siteCacheService.performCompleteCacheFlush();
  }

  /**
   * Reset update available status
   */
  public resetUpdateAvailable(): void {
    this.updateAvailableSubject.next(false);
  }

  /**
   * Get comprehensive version information for debugging
   */
  public async getVersionInfo(): Promise<any> {
    const cacheInfo = await this.siteCacheService.getCacheInfo();
    
    return {
      displayVersion: this.getCurrentDisplayVersion(),
      backendVersion: this.backendVersionSubject.value,
      serviceWorkerVersion: this.serviceWorkerVersionSubject.value,
      updateAvailable: this.updateAvailableSubject.value,
      cacheInfo,
      timestamp: new Date().toISOString()
    };
  }
} 